<template>
    <div
    ref="textblocksRef"
    class="text-blocks-wrapper"
    :class="[params.classes, {
        'apply-ellipsis': applyEllipsis === true,
        'break-all': !applyEllipsis && !JSON.stringify(params.content)?.includes(' '),
        'with-right-button':  rightButtonIcon
    }]">
        <!-- Label -->
        <label-generic
        v-if="params.label"
        :text="params.label"
        :multipleLines="params.labelHasMultipleLines"
        :mandatory="params.mandatory"
        :tooltipInfo="params.tooltipInfo"/>
        <!-- Content -->
        <div class="content-wrapper" :class="{'with-button-icon': rightButtonIcon}">
            <!-- Icon -->
            <icon v-if="props.icon" class="icon" :class="colorIconPreset"/>
            <!-- Content to inject in v-html-->
            <div v-if="params.htmlContent"
            class="content"
            :class="{'apply-ellipsis': applyEllipsis === true}"
            v-mask="mask"
            v-html="params.htmlContent || preventEmptyDefaultBehaviour ? params.htmlContent : '-'"/>
            <!-- content simple not injected to v-html -->
            <div v-else :key="params.content" class="content" :class="{'apply-ellipsis': applyEllipsis === true}" v-mask="mask">
                {{ params.content || preventEmptyDefaultBehaviour ? params.content : '-' }}
            </div>
            <!-- Quaternary Right Button -->
            <buttons-quaternary
            v-if="rightButtonIcon && rightButtonVariant === 'quaternary'"
            class="right-icon-button"
            buttonSize="small"
            :icon="rightButtonIcon"
            @clicked="emit('rightButtonClicked')"/>
            <!-- Solo Icon Button on Right -->
            <div v-if="rightButtonIcon && rightButtonVariant === 'soloIcon'" class="right-icon-button" @click.stop="emit('rightButtonClicked')">
				<right-button-icon-component class="icon"/>
            </div>
        </div>
    </div>
</template>

<script setup>
import LabelGeneric from '@/components/generic/labels/Generic'
import ButtonsQuaternary from '@/components/generic/buttons/Quaternary'

/* Vue */
import { ref, computed, defineAsyncComponent, onMounted } from 'vue'
import { iconModules } from '@/assets/icons/iconModules'

/* props received
@params { Object } with:
	- label { String}
    - labelHasMultipleLines { Boolean }
	- mandatory { Boolean } break label into multiple lines
    - classes {String} - adds extra classes to our text-block-generic, classes being handle: ['capitalize', 'amount-icon', 'primary', 'negative', 'extra-large']
    - content {String} - content to be displayed
@icon { String }
@colorIcon { String } any color for icon including var(--negative)
@colorIconPreset { String } - class to apply to icon, possible valus: [info, success, error, alert, warning]
@applyEllipsis { Boolean } - instead of the field growing vertically with the content, we can apply ellipsis
@mask {Object} - mask to apply in our content
@preventEmptyDefaultBehaviour { Boolean } - when its true, it doesn't write the '-' when content has no value
@rightButtonVariant {String} [quaternary (default), soloIcon]
*/

/* Emmitters */
const emit = defineEmits(['rightButtonClicked'])

const props = defineProps({
	params: Object,
    icon: String,
    colorIcon: String,
    colorIconPreset: String,
    applyEllipsis: Boolean,
    mask: Object,
    preventEmptyDefaultBehaviour: Boolean,
    rightButtonIcon: String,
    rightButtonVariant: { type: String, default: 'quaternary' }
})

/* Computed for icon */
const icon = computed(() => {
    const icon = props.icon
	return defineAsyncComponent(iconModules[`/src/assets/icons/${icon}.svg`])
})

/* Computed */
const rightButtonIconComponent = computed(() => {
    const icon = props.rightButtonIcon
	return defineAsyncComponent(iconModules[`/src/assets/icons/${icon}.svg`])
})

/* ApplicationTab Ref Access */
const textblocksRef = ref('')

onMounted(() => {
    if (!props.colorIcon) return
    const textBlockRef = textblocksRef.value
    textBlockRef.style.setProperty('--svgDetailColor', props.colorIcon)
})
</script>

<style lang="scss" scoped>
.text-blocks-wrapper {
	width: 100%;
    height: fit-content;

    --svgDetailColor: var(--secondary-variant-13);

    &.apply-ellipsis {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        .content-wrapper {
            .content {
                text-overflow: ellipsis;
            }
        }
    }

    &.break-word {
        .content-wrapper {
            .content {
                text-overflow:clip;
                white-space:normal;
            }
        }
    }

    &.with-right-button {
        padding-right: 56px;
    }

    &.break-all {
		.content-wrapper {
            .content {
                text-overflow:clip;
                white-space:normal;
                word-break: break-word;
            }
        }
    }

    &.capitalize {
        .content-wrapper {
            .content {
                text-transform: capitalize;
            }
        }
    }

    &.amount-icon {
        .content-wrapper {
            align-items: center;

            :deep(svg) {
                width: 16px;
                height: 16px;
                margin-top: 0;
            }
        }
    }

    &.primary,
    &.negative {
        .content-wrapper {
            .content {
                @include H3-Regular;
                line-height: 28px;
            }
        }
    }

    &.primary {
        .content-wrapper {
            .content {
                color: var(--primary);
            }
        }

        &.extra-large {
            .content-wrapper {
                .content {
                    @include H2-Regular;
                    line-height: 36px;
                    min-height: 36px;
                }
            }
        }
    }

    &.negative {
        .content-wrapper {
            .content {
                color: var(--negative);
            }
        }
    }

    .content-wrapper {
        display: flex;
        gap: 4px;

        margin-top: 8px;
        position: relative;

        .right-icon-button {
            bottom: 0;
            right: 0;

            position: absolute;
            cursor: pointer;

            &.button-quaternary {
                bottom: -2px;
            }

            :deep() svg {
                width: 20px;
                height: 20px;

                :deep(.change-color-fill) {
                    fill: var(--secondary-dark-3);
                }
            }
        }

        .content {
            @include H6-Regular;
            line-height: 18px;
            color: var(--secondary-dark-2);
            overflow: hidden;

            &.apply-ellipsis {
                text-overflow: ellipsis;
            }
        }

        &.with-button-icon {
            padding-right: 48px;
        }
    }

    :deep(svg) {
        width: 16px;
        height: 16px;
        flex-shrink: 0;

        .change-color {
            stroke: var(--svgDetailColor);
        }

        .change-color-fill {
            fill: var(--svgDetailColor);
        }

        &.positive {
            .change-color {
                stroke: var(--positive);
            }
            .change-color-fill {
                fill: var(--positive);
            }
        }

        &.info, &.secondary {
            .change-color {
                stroke: var(--secondary);
            }
            .change-color-fill {
                fill: var(--secondary);
            }
        }

        &.negative {
            .change-color {
                stroke: var(--negative);
            }
            .change-color-fill {
                fill: var(--negative);
            }
        }

        &.alert, &.warning {
            .change-color {
                stroke: var(--warning);
            }
            .change-color-fill {
                fill: var(--warning);
            }
        }
    }
}
</style>
